import React, { useState, useEffect } from 'react'
import AdminLayout from '../../components/admin/AdminLayout'
import { useAuth } from '../../context/AuthContext'
import api, { API_BASE, getImageUrl } from '../../utils/api'

interface ProjectDetail {
    _id: string;
    label: string;
    icon: string;
}

export default function AdminProjectDetails() {
    const { token } = useAuth()
    const [details, setDetails] = useState<ProjectDetail[]>([])
    const [loading, setLoading] = useState(true)
    const [label, setLabel] = useState('')
    const [icon, setIcon] = useState('')
    const [iconFile, setIconFile] = useState<File | null>(null)
    const [iconPreview, setIconPreview] = useState('')
    const [submitting, setSubmitting] = useState(false)
    const [editId, setEditId] = useState<string | null>(null)
    const [msg, setMsg] = useState<{ type: 'success' | 'error', text: string } | null>(null)

    useEffect(() => {
        fetchDetails()
    }, [])

    const fetchDetails = async () => {
        try {
            const res = await fetch(`${API_BASE}/project-details`)
            const data = await res.json()
            if (Array.isArray(data)) setDetails(data)
        } catch (err) {
            console.error(err)
        } finally {
            setLoading(false)
        }
    }

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault()
        if (!label || !token) return
        setSubmitting(true)
        setMsg(null)

        try {
            let iconUrl = iconPreview || icon
            if (iconFile && token) {
                const urls = await api.uploadImages(token, [iconFile])
                if (urls.urls && urls.urls[0]) {
                    iconUrl = urls.urls[0]
                }
            }

            const url = editId ? `${API_BASE}/project-details/${editId}` : `${API_BASE}/project-details`
            const method = editId ? 'PUT' : 'POST'

            const res = await fetch(url, {
                method,
                headers: {
                    'Content-Type': 'application/json',
                    Authorization: `Bearer ${token}`
                },
                body: JSON.stringify({ label, icon: iconUrl })
            })

            if (res.ok) {
                setMsg({ type: 'success', text: editId ? 'Project detail updated successfully!' : 'Project detail added successfully!' })
                setLabel('')
                setIcon('')
                setIconFile(null)
                setIconPreview('')
                setEditId(null)
                fetchDetails()
            } else {
                const errData = await res.json()
                setMsg({ type: 'error', text: errData.message || 'Failed to add detail' })
            }
        } catch (err) {
            setMsg({ type: 'error', text: 'Server error. Please try again.' })
        } finally {
            setSubmitting(false)
        }
    }

    const handleDelete = async (id: string) => {
        if (!token || !window.confirm('Are you sure you want to delete this project detail?')) return
        try {
            const res = await fetch(`${API_BASE}/project-details/${id}`, {
                method: 'DELETE',
                headers: { Authorization: `Bearer ${token}` }
            })
            if (res.ok) {
                setDetails(prev => prev.filter(d => d._id !== id))
                setMsg({ type: 'success', text: 'Project detail deleted.' })
            }
        } catch (err) {
            console.error(err)
        }
    }

    const handleEdit = (d: ProjectDetail) => {
        setLabel(d.label)
        setIcon(d.icon.startsWith('fa') ? d.icon : '')
        setIconPreview(d.icon)
        setIconFile(null)
        setEditId(d._id)
        setMsg(null)
        window.scrollTo({ top: 0, behavior: 'smooth' })
    }

    return (
        <AdminLayout>
            <div className="mb-8">
                <h1 className="text-3xl font-bold text-slate-900 dark:text-white mb-2">Manage Project Extras</h1>
                <p className="text-blue-950 dark:text-blue-950">Define additional labels and icons to show as checkboxes in the project wizard.</p>
            </div>

            {msg && (
                <div className={`mb-6 p-4 rounded-2xl flex items-center gap-3 animate-in fade-in slide-in-from-top-4 duration-300 ${
                    msg.type === 'success' 
                        ? 'bg-emerald-50 dark:bg-emerald-900/20 text-emerald-700 dark:text-emerald-400 border border-emerald-100 dark:border-emerald-800' 
                        : 'bg-rose-50 dark:bg-rose-900/20 text-rose-700 dark:text-rose-400 border border-rose-100 dark:border-rose-800'
                }`}>
                    <i className={`fa-solid ${msg.type === 'success' ? 'fa-circle-check' : 'fa-circle-exclamation'}`}></i>
                    <p className="text-sm font-medium">{msg.text}</p>
                </div>
            )}

            <div className="grid grid-cols-1 lg:grid-cols-12 gap-8">
                {/* Form Section */}
                <div className="lg:col-span-4">
                    <div className="bg-white dark:bg-slate-900 rounded-3xl border border-blue-900/10 dark:border-slate-800 shadow-xl shadow-slate-200/50 dark:shadow-none overflow-hidden sticky top-24">
                        <div className="p-6 border-b border-blue-900/10 dark:border-slate-800 flex items-center justify-between bg-white/50 dark:bg-slate-800/50">
                            <h2 className="text-lg font-bold text-slate-900 dark:text-white">
                                {editId ? 'Edit Detail' : 'Add New Detail'}
                            </h2>
                            {editId && (
                                <button 
                                    onClick={() => {
                                        setEditId(null)
                                        setLabel('')
                                        setIcon('')
                                        setIconFile(null)
                                        setIconPreview('')
                                    }}
                                    className="text-xs font-semibold text-rose-500 hover:text-rose-600 px-3 py-1.5 rounded-lg bg-rose-50 dark:bg-rose-950/30 transition-colors"
                                >
                                    Cancel
                                </button>
                            )}
                        </div>
                        <div className="p-6">
                            <form onSubmit={handleSubmit} className="space-y-6">
                                <div>
                                    <label className="block text-xs font-bold text-blue-950 dark:text-blue-950 uppercase tracking-wider mb-2">
                                        Label Name
                                    </label>
                                    <input 
                                        type="text" 
                                        className="w-full px-4 py-3 rounded-xl bg-white dark:bg-slate-800 border-none focus:ring-2 focus:ring-blue-500 dark:text-white transition-all placeholder:text-blue-950"
                                        value={label} 
                                        onChange={e => setLabel(e.target.value)} 
                                        placeholder="e.g., Covered Parking" 
                                        required 
                                    />
                                </div>

                                <div>
                                    <label className="block text-xs font-bold text-blue-950 dark:text-blue-950 uppercase tracking-wider mb-2">
                                        Icon (Web Icon or Upload)
                                    </label>
                                    <div className="flex gap-2">
                                        <div className="flex-1">
                                            <input 
                                                type="text" 
                                                className="w-full px-4 py-3 rounded-xl bg-white dark:bg-slate-800 border-none focus:ring-2 focus:ring-blue-500 dark:text-white transition-all placeholder:text-blue-950"
                                                value={icon} 
                                                onChange={e => {
                                                    setIcon(e.target.value)
                                                    setIconPreview(e.target.value)
                                                    setIconFile(null)
                                                }} 
                                                placeholder="fa-solid fa-car" 
                                            />
                                        </div>
                                        <div className="relative group shrink-0">
                                            <div className="w-12 h-12 rounded-xl border-2 border-dashed border-blue-900/10 dark:border-slate-800 flex items-center justify-center bg-white dark:bg-slate-800 overflow-hidden shadow-inner transition-all hover:border-blue-500">
                                                {iconPreview ? (
                                                    (iconPreview.startsWith('http') || iconPreview.includes('/') || iconPreview.includes('.')) ? (
                                                        <img src={getImageUrl(iconPreview)} alt="Preview" className="w-full h-full object-contain p-1" />
                                                    ) : (
                                                        <i className={`${iconPreview} text-blue-500 text-lg`}></i>
                                                    )
                                                ) : (
                                                    <i className="fa-solid fa-cloud-arrow-up text-blue-950"></i>
                                                )}
                                                <input 
                                                    type="file" 
                                                    className="absolute inset-0 opacity-0 cursor-pointer z-10"
                                                    onChange={e => {
                                                        const file = e.target.files?.[0]
                                                        if (file) {
                                                            setIconFile(file)
                                                            setIconPreview(URL.createObjectURL(file))
                                                            setIcon('')
                                                        }
                                                    }}
                                                />
                                            </div>
                                        </div>
                                    </div>
                                    <p className="mt-2 text-[10px] text-blue-950 dark:text-blue-950 pl-1 italic">
                                        Enter a class name (e.g. `fa-solid fa-star`) or upload an image.
                                    </p>
                                </div>

                                <button 
                                    className={`
                                        w-full py-4 rounded-xl font-bold shadow-lg transition-all flex items-center justify-center gap-2
                                        ${submitting 
                                            ? 'bg-blue-50 dark:bg-slate-800 text-blue-950 cursor-not-allowed' 
                                            : 'bg-blue-600 hover:bg-blue-700 text-white shadow-blue-500/25'}
                                    `}
                                    type="submit" 
                                    disabled={submitting}
                                >
                                    {submitting ? (
                                        <>
                                            <svg className="animate-spin h-5 w-5 text-current" viewBox="0 0 24 24">
                                                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none"></circle>
                                                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                                            </svg>
                                            <span>Processing...</span>
                                        </>
                                    ) : (
                                        <>
                                            <i className={`fa-solid ${editId ? 'fa-pen-to-square' : 'fa-plus'}`}></i>
                                            <span>{editId ? 'Update Detail' : 'Add Detail'}</span>
                                        </>
                                    )}
                                </button>
                            </form>
                        </div>
                    </div>
                </div>

                {/* Table Section */}
                <div className="lg:col-span-8">
                    <div className="bg-white dark:bg-slate-900 rounded-3xl border border-blue-900/10 dark:border-slate-800 shadow-xl shadow-slate-200/50 dark:shadow-none overflow-hidden">
                        <div className="p-6 border-b border-blue-900/10 dark:border-slate-800 flex items-center justify-between">
                            <div>
                                <h2 className="text-lg font-bold text-slate-900 dark:text-white">Active Additional Details</h2>
                                <p className="text-xs text-blue-950 mt-1">{details.length} options configured</p>
                            </div>
                        </div>

                        {loading ? (
                            <div className="p-12 text-center text-blue-950">
                                <div className="animate-spin w-8 h-8 border-4 border-blue-500 border-t-transparent rounded-full mx-auto mb-4"></div>
                                <p className="text-sm font-medium">Loading details...</p>
                            </div>
                        ) : details.length === 0 ? (
                            <div className="p-12 text-center">
                                <div className="w-16 h-16 bg-white dark:bg-slate-800/50 rounded-full flex items-center justify-center mx-auto mb-4">
                                    <i className="fa-solid fa-circle-nodes text-blue-100 dark:text-slate-700 text-2xl"></i>
                                </div>
                                <p className="text-blue-950 dark:text-blue-950 font-medium">No details found</p>
                                <p className="text-sm text-blue-950 mt-1">Add your first detail to get started</p>
                            </div>
                        ) : (
                            <div className="overflow-x-auto">
                                <table className="w-full">
                                    <thead>
                                        <tr className="bg-white/50 dark:bg-slate-800/30">
                                            <th className="px-6 py-4 text-center text-xs font-bold text-blue-950 dark:text-blue-950 uppercase tracking-wider w-24">Icon</th>
                                            <th className="px-6 py-4 text-left text-xs font-bold text-blue-950 dark:text-blue-950 uppercase tracking-wider">Label</th>
                                            <th className="px-6 py-4 text-right text-xs font-bold text-blue-950 dark:text-blue-950 uppercase tracking-wider">Actions</th>
                                        </tr>
                                    </thead>
                                    <tbody className="divide-y divide-slate-100 dark:divide-slate-800">
                                        {details.map(d => (
                                            <tr key={d._id} className="group hover:bg-white/50 dark:hover:bg-slate-800/20 transition-all">
                                                <td className="px-6 py-4 text-center">
                                                    <div className="w-12 h-12 rounded-xl bg-white dark:bg-slate-800 p-2 flex items-center justify-center border border-blue-900/10 dark:border-slate-700 shadow-sm mx-auto transition-transform group-hover:scale-110">
                                                        {d.icon && (d.icon.startsWith('http') || d.icon.includes('/') || d.icon.includes('.')) ? (
                                                            <img src={getImageUrl(d.icon)} alt={d.label} className="w-full h-full object-contain" />
                                                        ) : (
                                                            <i className={`${d.icon || 'fa-solid fa-circle-check'} text-blue-500 text-xl`}></i>
                                                        )}
                                                    </div>
                                                </td>
                                                <td className="px-6 py-4">
                                                    <span className="text-sm font-bold text-slate-900 dark:text-white group-hover:text-blue-600 dark:group-hover:text-blue-400 transition-colors">
                                                        {d.label}
                                                    </span>
                                                </td>
                                                <td className="px-6 py-4 text-right">
                                                    <div className="flex justify-end gap-2">
                                                        <button 
                                                            className="w-10 h-10 rounded-xl bg-blue-50 dark:bg-blue-900/20 text-blue-600 dark:text-blue-400 hover:bg-blue-600 hover:text-white transition-all flex items-center justify-center shadow-sm"
                                                            onClick={() => handleEdit(d)}
                                                        >
                                                            <i className="fa-solid fa-pen-to-square text-xs"></i>
                                                        </button>
                                                        <button 
                                                            className="w-10 h-10 rounded-xl bg-rose-50 dark:bg-rose-900/20 text-rose-600 dark:text-rose-400 hover:bg-rose-600 hover:text-white transition-all flex items-center justify-center shadow-sm"
                                                            onClick={() => handleDelete(d._id)}
                                                        >
                                                            <i className="fa-solid fa-trash-can text-xs"></i>
                                                        </button>
                                                    </div>
                                                </td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </div>
                        )}
                    </div>
                </div>
            </div>
        </AdminLayout>
    )
}


